import React, { useCallback, useEffect, useMemo, useState } from "react";
import { tWithKeyAttr as t } from "../../../../i18nHelper";
import i18n from "../../../../i18n";
import styles from "./DeviceConfigurationModal.module.css";
import modalStyles from "../EditAppointmentModal/EditAppointmentModal.module.css";
import { AppointmentDevice, DeviceCategory, DeviceConfig, StaffDevice } from "../../../../services/types/device.types";
import DeviceConfigForm from "../../../../components/forms/deviceConfigForm/DeviceConfigForm";
import {
  DEVICE_CATEGORIES,
  getDefaultDeviceConfig,
  getDeviceCapabilities,
  normalizeDeviceConfig,
  staffDeviceToAppointmentDevice,
  validateDeviceConfig,
} from "../../../../utils/deviceConfiguration";

interface DeviceConfigurationModalProps {
  devices: StaffDevice[];
  initialDevice?: AppointmentDevice | null;
  onConfirm: (device: AppointmentDevice) => void;
  onCancel: () => void;
}

function DeviceConfigurationModal({
  devices,
  initialDevice,
  onConfirm,
  onCancel,
}: DeviceConfigurationModalProps) {
  const isRtl = i18n.language === "he";
  const [selectedDeviceId, setSelectedDeviceId] = useState<number>(
    initialDevice?.deviceContactId ?? devices[0]?.deviceContactId ?? 0,
  );
  const selectedDevice = devices.find((d) => d.deviceContactId === selectedDeviceId);

  const availableModalities = useMemo(
    () => getDeviceCapabilities(selectedDevice?.deviceConfig),
    [selectedDevice],
  );

  const [modality, setModality] = useState<DeviceCategory>(
    initialDevice?.category ?? availableModalities[0] ?? "nf",
  );

  const [sessionConfig, setSessionConfig] = useState<DeviceConfig>(() =>
    normalizeDeviceConfig(
      initialDevice ?? getDefaultDeviceConfig(initialDevice?.category ?? availableModalities[0] ?? "nf"),
    ),
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (initialDevice?.deviceContactId) {
      setSelectedDeviceId(initialDevice.deviceContactId);
      if (initialDevice.category) setModality(initialDevice.category);
      setSessionConfig(normalizeDeviceConfig(initialDevice));
    }
  }, [initialDevice]);

  const handleDeviceChange = useCallback(
    (deviceId: number) => {
      setSelectedDeviceId(deviceId);
      const device = devices.find((d) => d.deviceContactId === deviceId);
      const caps = getDeviceCapabilities(device?.deviceConfig);
      const first = caps[0] ?? "nf";
      setModality(first);
      setSessionConfig(getDefaultDeviceConfig(first));
      setValidationError(null);
    },
    [devices],
  );

  const handleModalityChange = useCallback((next: DeviceCategory) => {
    setModality(next);
    setSessionConfig(getDefaultDeviceConfig(next));
    setValidationError(null);
  }, []);

  const handleConfirm = () => {
    const configWithCategory = { ...sessionConfig, category: modality };
    const errorKey = validateDeviceConfig(configWithCategory, { requireSessionWaves: true });
    if (errorKey) {
      setValidationError(errorKey);
      return;
    }
    if (!selectedDevice) {
      setValidationError("DEVICE_NOT_FOUND");
      return;
    }
    onConfirm(staffDeviceToAppointmentDevice(selectedDevice, configWithCategory));
  };

  return (
    <div
      className={modalStyles.modalOverlay}
      style={{ zIndex: 10002 }}
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div className={`${modalStyles.modal} ${styles.modal}`} dir={isRtl ? "rtl" : "ltr"}>
        <div className={modalStyles.modalHeader}>
          <div className={modalStyles.modalTitle}>
            <span>🔌</span>
            <span>{t("DEVICE_SESSION_TITLE")}</span>
          </div>
          <button type="button" className={modalStyles.closeBtn} onClick={onCancel}>
            ×
          </button>
        </div>

        <div className={modalStyles.modalBody}>
          <p className={styles.hint}>{t("DEVICE_SESSION_HINT")}</p>

          <div className={modalStyles.formGroup}>
            <label className={modalStyles.label}>
              <span className={modalStyles.labelIcon}>🔌</span>
              {t("SELECT_DEVICE")}
            </label>
            <select
              className={modalStyles.input}
              value={selectedDeviceId || ""}
              onChange={(e) => handleDeviceChange(Number(e.target.value))}
            >
              {devices.map((d) => (
                <option key={d.deviceContactId} value={d.deviceContactId}>
                  {d.deviceName}
                </option>
              ))}
            </select>
          </div>

          {availableModalities.length > 0 && (
            <div className={modalStyles.formGroup}>
              <label className={modalStyles.label}>{t("DEVICE_SELECT_MODALITY")}</label>
              <select
                className={modalStyles.input}
                value={modality}
                onChange={(e) => handleModalityChange(e.target.value as DeviceCategory)}
              >
                {availableModalities.map((cap) => {
                  const meta = DEVICE_CATEGORIES.find((c) => c.value === cap);
                  return (
                    <option key={cap} value={cap}>
                      {meta ? t(meta.labelKey) : cap}
                    </option>
                  );
                })}
              </select>
            </div>
          )}

          <DeviceConfigForm
            value={sessionConfig}
            onChange={(update) => {
              setSessionConfig((prev) =>
                typeof update === "function" ? update(prev) : update,
              );
              setValidationError(null);
            }}
            fixedCategory={modality}
            requireSessionWaves
          />

          {validationError && (
            <div className={styles.error}>{t(validationError)}</div>
          )}
        </div>

        <div className={modalStyles.modalFooter}>
          <button type="button" className={modalStyles.cancelBtn} onClick={onCancel}>
            {t("CANCEL")}
          </button>
          <button type="button" className={modalStyles.saveBtn} onClick={handleConfirm}>
            {t("CONFIRM")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeviceConfigurationModal;
